import CRMSourcePolicy from '../components/CRMSourcePolicy';
import React, { useState, useEffect } from 'react';
import { crmAPI } from '../services/api';
import { Plug, Save, Users, ShoppingCart, Trash2, Download, Check, AlertCircle, Copy, HelpCircle } from 'lucide-react';
import SearchableSelect from '../components/SearchableSelect';
import toast from 'react-hot-toast';

export default function CRMPage() {
  const [integration, setIntegration] = useState(null);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHelp, setShowHelp] = useState(false);


  const [crmType, setCrmType] = useState('bitrix24');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [contactBalanceField, setContactBalanceField] = useState('');
  const [dealBonusUsedField, setDealBonusUsedField] = useState('');
  const [dealBonusEarnedField, setDealBonusEarnedField] = useState('');
  const [incomingSecret, setIncomingSecret] = useState('');
  const [secretSet, setSecretSet] = useState(false);


  const [contactFields, setContactFields] = useState({});
  const [genderField, setGenderField] = useState('');
  const [genderMaleValue, setGenderMaleValue] = useState('');
  const [genderFemaleValue, setGenderFemaleValue] = useState('');
  const [consentMode, setConsentMode] = useState('false_for_all');
  const [consentSmsField, setConsentSmsField] = useState('');
  const [consentEmailField, setConsentEmailField] = useState('');


  const [stages, setStages] = useState([]);
  const [stagesLoading, setStagesLoading] = useState(false);
  const [createStages, setCreateStages] = useState([]);
  const [confirmStages, setConfirmStages] = useState([]);
  const [cancelStages, setCancelStages] = useState([]);


  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [currentTaskId, setCurrentTaskId] = useState(null);


  const incomingUrl = integration ? `${window.location.origin}/api/v1/crm/webhook/${integration.account_id || ''}` : '';

  const load = () => {
    Promise.all([crmAPI.types(), crmAPI.get().catch(() => null)])
      .then(([tRes, iRes]) => {
        setTypes(tRes.data);
        if (iRes?.data) {
          setIntegration(iRes.data);
          setWebhookUrl(iRes.data.webhook_url || '');
          setCrmType(iRes.data.crm_type);
          setContactBalanceField(iRes.data.contact_balance_field || '');
          setDealBonusUsedField(iRes.data.deal_bonus_used_field || '');
          setDealBonusEarnedField(iRes.data.deal_bonus_earned_field || '');
          const fm = iRes.data.field_mapping || {};
          setGenderField(fm.gender_field || '');
          setGenderMaleValue(fm.gender_male_value || '');
          setGenderFemaleValue(fm.gender_female_value || '');
          setConsentMode(fm.import_consents || 'false_for_all');
          setConsentSmsField(fm.consent_sms_field || '');
          setConsentEmailField(fm.consent_email_field || '');
          setSecretSet(!!iRes.data.incoming_secret_set);
          setIncomingSecret('');  // токен с бэка не приходит; пустое = "не менять"
          const m = iRes.data.stage_mapping || {};
          setCreateStages(m.create_stages || []);
          setConfirmStages(m.confirm_stages || []);
          setCancelStages(m.cancel_stages || []);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const loadStages = async () => {
    setStagesLoading(true);
    try {
      const res = await crmAPI.stages();
      setStages(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Не удалось получить стадии');
    }
    finally { setStagesLoading(false); }
  };

  useEffect(() => {
    if (integration) {
      loadStages();
      crmAPI.contactFields().then(res => setContactFields(res.data || {})).catch(() => {});
    }
  }, [integration]);

  const handleSave = async () => {
    if (!webhookUrl) { toast.error('Введите webhook URL'); return; }
    setSaving(true);
    try {
      const stage_mapping = {
        create_stages: createStages,
        confirm_stages: confirmStages,
        cancel_stages: cancelStages,
      };
      const payload = {
        webhook_url: webhookUrl,
        stage_mapping,
        contact_balance_field: contactBalanceField || null,
        deal_bonus_used_field: dealBonusUsedField || null,
        deal_bonus_earned_field: dealBonusEarnedField || null,
        field_mapping: {
          gender_field: genderField || null,
          gender_male_value: genderMaleValue || null,
          gender_female_value: genderFemaleValue || null,
          import_consents: consentMode,
          consent_sms_field: consentSmsField || null,
          consent_email_field: consentEmailField || null,
        },
        ...(incomingSecret.trim() ? { incoming_secret: incomingSecret.trim() } : {}),
      };
      if (integration) {
        await crmAPI.update(payload);
      } else {
        await crmAPI.create({ crm_type: crmType, ...payload });
      }
      toast.success('Сохранено');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Удалить CRM-интеграцию?')) return;
    try {
      await crmAPI.delete();
      toast.success('Удалено');
      setIntegration(null);
      setWebhookUrl(''); setCreateStages([]); setConfirmStages([]); setCancelStages([]);
      setIncomingSecret(''); setSecretSet(false);
    } catch { toast.error('Ошибка'); }
  };

  const handleClearSecret = async () => {
    if (!window.confirm('Отключить защиту вебхука?\n\nВходящие вебхуки от Bitrix24 перестанут проверяться на токен. Интеграция вернётся в legacy-режим (любой запрос на webhook URL будет приниматься).')) return;
    try {
      await crmAPI.clearSecret();
      toast.success('Защита отключена');
      setIncomingSecret(''); setSecretSet(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
  };

  const pollTask = async (taskId, type) => {
    setCurrentTaskId(taskId);
    let attempts = 0;
    const maxAttempts = 600;

    const tick = async () => {
      attempts++;
      if (attempts > maxAttempts) {
        toast.error('Превышено время ожидания');
        setImporting(false); setCurrentTaskId(null);
        return;
      }

      try {
        const res = await crmAPI.taskStatus(taskId);
        const data = res.data;

        if (data.status === 'pending' || data.status === 'running') {
          setImportResult({
            type, status: data.status,
            ...(data.progress || {}),
            running: true,
          });
          setTimeout(tick, 3000);
        } else if (data.status === 'success') {
          setImportResult({ type, status: 'success', ...(data.progress || {}) });
          toast.success(`${type === 'clients' ? 'Клиенты' : 'Заказы'}: создано ${data.progress?.created || 0}, обновлено ${data.progress?.updated || 0}`);
          setImporting(false); setCurrentTaskId(null);
          load();
        } else if (data.status === 'cancelled') {
          setImportResult({ type, status: 'cancelled', ...(data.progress || {}) });
          toast('Импорт отменён', { icon: '⏸' });
          setImporting(false); setCurrentTaskId(null);
          load();
        } else {
          toast.error(data.message || 'Ошибка импорта');
          setImportResult({ type, status: 'failed', message: data.message });
          setImporting(false); setCurrentTaskId(null);
        }
      } catch (err) {
        toast.error('Ошибка получения статуса');
        setImporting(false); setCurrentTaskId(null);
      }
    };

    tick();
  };

  const handleCancelImport = async () => {
    if (!currentTaskId) return;
    if (!window.confirm('Остановить импорт? Уже импортированные данные останутся.')) return;
    try {
      await crmAPI.cancelTask(currentTaskId);
      toast.success('Запрошена остановка');
    } catch { toast.error('Ошибка отмены'); }
  };

  const handleImportClients = async (limit = 0) => {
    setImporting(true); setImportResult(null);
    try {
      const res = await crmAPI.importClients(limit);
      toast.success('Импорт запущен в фоне');
      pollTask(res.data.task_id, 'clients');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
      setImporting(false);
    }
  };

  const handleImportOrders = async (options = {}) => {
    setImporting(true); setImportResult(null);
    try {
      const res = await crmAPI.importOrders(options);
      toast.success('Импорт запущен в фоне');
      pollTask(res.data.task_id, 'orders');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
      setImporting(false);
    }
  };

  const toggleStage = (stageId, list, setList) => {
    if (list.includes(stageId)) setList(list.filter(s => s !== stageId));
    else setList([...list, stageId]);
  };

  const stageInAnyList = (stageId) =>
    createStages.includes(stageId) || confirmStages.includes(stageId) || cancelStages.includes(stageId);

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Загрузка...</div>;

  return (
    <div className="animate-in">
      <div className="page-header">
        <h1 className="page-title">CRM-интеграция</h1>
        <p className="page-subtitle">Синхронизация сделок и клиентов</p>
      </div>

      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 16 }} onClick={() => setShowHelp(!showHelp)}>
        <HelpCircle size={14} /> {showHelp ? 'Скрыть инструкцию' : 'Как подключить Битрикс24?'}
      </button>

      {integration&&<CRMSourcePolicy/>}
      {showHelp && (
        <div className="card" style={{ padding: 20, marginBottom: 20, fontSize: 13, lineHeight: 1.7 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'var(--accent)' }}>
            Пошаговая инструкция подключения Битрикс24
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>
              Шаг 1. Входящий вебхук (Б24 → SEGMIRA)
            </div>
            <div style={{ color: 'var(--text-muted)', paddingLeft: 8 }}>
              Нужен, чтобы SEGMIRA могла получать данные из Б24 (импорт клиентов и сделок).<br/><br/>
              1. В Битрикс24 откройте: <code>Разработчикам</code> → <code>Другое</code> → <code>Входящий вебхук</code><br/>
              2. Нажмите «Добавить вебхук»<br/>
              3. В правах (scope) выберите: <code>crm</code><br/>
              4. Нажмите «Сохранить» — появится URL вебхука вида<br/>
              <code style={{ fontSize: 11, background: 'var(--bg-secondary)', padding: '2px 6px', borderRadius: 3 }}>
                https://company.bitrix24.ru/rest/1/abc123xyz/
              </code><br/>
              5. Скопируйте этот URL и вставьте в поле «Webhook URL» ниже<br/>
              6. Нажмите «Подключить» — загрузятся стадии сделок
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>
              Шаг 2. Настройка маппинга стадий
            </div>
            <div style={{ color: 'var(--text-muted)', paddingLeft: 8 }}>
              Для каждой стадии сделки в Б24 выберите что делать в SEGMIRA:<br/><br/>
              <strong style={{ color: 'var(--info)' }}>Создать</strong> — сделка в этой стадии появится как новый заказ (например «Новая», «В работе»)<br/>
              <strong style={{ color: 'var(--success)' }}>Подтвердить</strong> — заказ будет отмечен как выполненный (например «Сделка успешна»)<br/>
              <strong style={{ color: 'var(--danger)' }}>Отменить</strong> — заказ будет отменён (например «Сделка провалена»)<br/><br/>
              Можно отмечать несколько стадий для одного действия. Нажмите «Сохранить» после настройки.
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>
              Шаг 3. Исходящий вебхук (Б24 → SEGMIRA при изменениях)
            </div>
            <div style={{ color: 'var(--text-muted)', paddingLeft: 8 }}>
              Нужен, чтобы изменения сделок в Б24 сразу отражались в SEGMIRA без ручного импорта.<br/><br/>
              1. В Б24: <code>Разработчикам</code> → <code>Другое</code> → <code>Исходящий вебхук</code><br/>
              2. Нажмите «Добавить вебхук»<br/>
              3. В поле «URL вашего обработчика» вставьте адрес который появится после подключения (блок «Webhook для входящих событий» ниже)<br/>
              4. В событиях выберите:<br/>
              &nbsp;&nbsp;• <code>ONCRMDEALADD</code> — новая сделка<br/>
              &nbsp;&nbsp;• <code>ONCRMDEALUPDATE</code> — изменение сделки (смена стадии)<br/>
              5. Сохраните<br/><br/>
              Теперь любое изменение сделки в Б24 моментально обновит заказ в SEGMIRA.
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>
              Шаг 4. Первичный импорт
            </div>
            <div style={{ color: 'var(--text-muted)', paddingLeft: 8 }}>
              После подключения нажмите:<br/>
              • <strong>Импорт всех клиентов</strong> — загрузит все контакты из Б24 (с нормализацией телефонов)<br/>
              • <strong>Импорт всех сделок</strong> — загрузит сделки в соответствии с маппингом стадий<br/><br/>
              Дубликаты не создаются — повторный импорт обновит существующие записи.
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>
              Шаг 5. Синхронизация бонусов (опционально)
            </div>
            <div style={{ color: 'var(--text-muted)', paddingLeft: 8 }}>
              Если в Б24 ведётся учёт бонусов, их можно синхронизировать с SEGMIRA.<br/><br/>
              1. В Б24: <code>Настройки → CRM → Настройки форм и отчётов → Пользовательские поля</code><br/>
              2. В разделе «Контакты» создайте поле типа Число для баланса (например <code>UF_CRM_BONUS_BALANCE</code>)<br/>
              3. В разделе «Сделки» создайте два поля:<br/>
              &nbsp;&nbsp;• <code>UF_CRM_BONUS_USED</code> — списанные бонусы<br/>
              &nbsp;&nbsp;• <code>UF_CRM_BONUS_EARNED</code> — начисленные бонусы (кешбэк)<br/>
              4. Укажите коды этих полей в разделе «Синхронизация бонусов» ниже<br/>
              5. При импорте клиентов/сделок балансы и транзакции подтянутся из Б24<br/>
              6. Любое начисление/списание в SEGMIRA будет обновлять поле контакта в Б24 автоматически
            </div>
          </div>

          <div style={{ padding: 12, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)' }}>
            <strong style={{ color: 'var(--warning)' }}>💡 Важно:</strong> у контактов в Б24 должен быть указан телефон, иначе они не импортируются. Клиент в SEGMIRA идентифицируется по телефону.
          </div>
        </div>
      )}


      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <Plug size={20} style={{ color: 'var(--accent)' }} />
          <div style={{ fontSize: 14, fontWeight: 600 }}>Настройки подключения</div>
          {integration && <span className="badge badge-success">Подключено</span>}
        </div>

        <div className="form-group">
          <label className="form-label">CRM-система</label>
          <select className="form-input" value={crmType} onChange={e => setCrmType(e.target.value)} disabled={!!integration}>
            {types.map(t => <option key={t.type} value={t.type}>{t.label}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Webhook URL*</label>
          <input className="form-input" placeholder="https://company.bitrix24.ru/rest/1/TOKEN/"
            value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)}
            style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
            В Б24: Разработчикам → Другое → Входящий вебхук → Права: crm
          </div>
        </div>
      </div>


      {integration && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            Защита вебхука{' '}
            {secretSet
              ? <span className="badge badge-success">🔒 Защита настроена</span>
              : <span className="badge badge-danger">⚠️ Защита не настроена</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
            Application token из настроек исходящего вебхука Bitrix24. Каждый входящий
            вебхук будет проверяться на совпадение этого токена.
          </div>
          <div className="form-group">
            <label className="form-label">Application token</label>
            <input
              type="password"
              className="form-input"
              placeholder={secretSet
                ? 'Оставьте пустым, чтобы не менять'
                : 'Вставьте application_token из настроек исходящего вебхука Bitrix24'}
              value={incomingSecret}
              onChange={e => setIncomingSecret(e.target.value)}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
            <div style={{ fontSize: 11, color: 'var(--warning)', marginTop: 6 }}>
              ⚠️ Вписывайте токен здесь только после того, как исходящий вебхук в Bitrix24
              уже работает с этим токеном — иначе синхронизация заказов остановится.
            </div>
          </div>
          {secretSet && (
            <button className="btn btn-danger" onClick={handleClearSecret} style={{ marginTop: 8 }}>
              Отключить защиту
            </button>
          )}
        </div>
      )}

      {integration && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Маппинг стадий сделок</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
            Укажите какие стадии создают заказ, подтверждают или отменяют его
          </div>

          {stagesLoading && <div style={{ color: 'var(--text-muted)' }}>Загрузка стадий из Б24...</div>}

          {stages.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {stages.map(s => (
                <div key={s.STATUS_ID} style={{
                  padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                  background: 'var(--bg-secondary)', border: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{s.NAME}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{s.STATUS_ID}</div>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer' }}>
                    <input type="checkbox" checked={createStages.includes(s.STATUS_ID)}
                      onChange={() => toggleStage(s.STATUS_ID, createStages, setCreateStages)} />
                    <span style={{ color: 'var(--info)' }}>Создать</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer' }}>
                    <input type="checkbox" checked={confirmStages.includes(s.STATUS_ID)}
                      onChange={() => toggleStage(s.STATUS_ID, confirmStages, setConfirmStages)} />
                    <span style={{ color: 'var(--success)' }}>Подтвердить</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer' }}>
                    <input type="checkbox" checked={cancelStages.includes(s.STATUS_ID)}
                      onChange={() => toggleStage(s.STATUS_ID, cancelStages, setCancelStages)} />
                    <span style={{ color: 'var(--danger)' }}>Отменить</span>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      )}


      {integration && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            Маппинг полей контакта <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(опционально)</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
            {Object.keys(contactFields).length === 0
              ? 'Загружаются поля из Б24...'
              : `Доступно полей: ${Object.keys(contactFields).length}`}
          </div>


          <div className="form-group">
            <label className="form-label">Поле «Пол»</label>
            <SearchableSelect
              value={genderField}
              onChange={(v) => { setGenderField(v); setGenderMaleValue(''); setGenderFemaleValue(''); }}
              placeholder="Выберите поле из Б24..."
              options={Object.entries(contactFields).map(([code, info]) => ({
                value: code,
                label: info.formLabel || info.listLabel || info.title || code,
                sublabel: `${code} · ${info.type || ''}`,
              }))}
            />
          </div>


          {genderField && contactFields[genderField] && (
            (() => {
              const fieldInfo = contactFields[genderField];
              const isEnum = fieldInfo.type === 'enumeration' && Array.isArray(fieldInfo.items);

              if (isEnum) {
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginLeft: 16 }}>
                    <div className="form-group">
                      <label className="form-label">Значение «Мужской»</label>
                      <select className="form-input" value={genderMaleValue}
                        onChange={e => setGenderMaleValue(e.target.value)}>
                        <option value="">— выберите —</option>
                        {fieldInfo.items.map(item => (
                          <option key={item.ID} value={item.ID}>{item.VALUE}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Значение «Женский»</label>
                      <select className="form-input" value={genderFemaleValue}
                        onChange={e => setGenderFemaleValue(e.target.value)}>
                        <option value="">— выберите —</option>
                        {fieldInfo.items.map(item => (
                          <option key={item.ID} value={item.ID}>{item.VALUE}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              } else {
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginLeft: 16 }}>
                    <div className="form-group">
                      <label className="form-label">Значение «Мужской»</label>
                      <input className="form-input" placeholder="например M или 1"
                        style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
                        value={genderMaleValue} onChange={e => setGenderMaleValue(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Значение «Женский»</label>
                      <input className="form-input" placeholder="например F или 2"
                        style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
                        value={genderFemaleValue} onChange={e => setGenderFemaleValue(e.target.value)} />
                    </div>
                  </div>
                );
              }
            })()
          )}


          <div className="form-group">
            <label className="form-label">Согласия SMS/Email при импорте</label>
            <select className="form-input" value={consentMode} onChange={e => setConsentMode(e.target.value)}>
              <option value="false_for_all">Не давать согласий (безопасно)</option>
              <option value="true_for_all">Дать всем импортируемым согласия</option>
              <option value="by_field">Брать из полей Б24</option>
            </select>
          </div>

          {consentMode === 'by_field' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Поле согласия SMS</label>
                <SearchableSelect
                  value={consentSmsField}
                  onChange={setConsentSmsField}
                  placeholder="Выберите поле..."
                  options={Object.entries(contactFields).map(([code, info]) => ({
                    value: code, label: info.formLabel || info.listLabel || info.title || code, sublabel: `${code} · ${info.type || ''}`,
                  }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Поле согласия Email</label>
                <SearchableSelect
                  value={consentEmailField}
                  onChange={setConsentEmailField}
                  placeholder="Выберите поле..."
                  options={Object.entries(contactFields).map(([code, info]) => ({
                    value: code, label: info.formLabel || info.listLabel || info.title || code, sublabel: `${code} · ${info.type || ''}`,
                  }))}
                />
              </div>
            </div>
          )}

          <div style={{ fontSize: 11, color: 'var(--text-muted)', padding: 10, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', marginTop: 4 }}>
            ℹ️ Дата рождения (BIRTHDATE) — стандартное поле Б24, синхронизируется автоматически. Уровень лояльности пересчитается по сумме подтверждённых сделок при импорте.
          </div>
        </div>
      )}


      {integration && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            Синхронизация бонусов <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(опционально)</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
            Если в Б24 есть пользовательские поля для баланса и бонусов, укажите их коды — баланс будет синхронизироваться автоматически.
          </div>

          <div className="form-group">
            <label className="form-label">Код поля контакта с балансом</label>
            <input className="form-input" placeholder="UF_CRM_BONUS_BALANCE"
              style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
              value={contactBalanceField} onChange={e => setContactBalanceField(e.target.value)} />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Код пользовательского поля контакта в Б24. При импорте — баланс из этого поля подтянется в SEGMIRA. При любых операциях в SEGMIRA — обновится в Б24.
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Поле сделки: списано бонусов</label>
              <input className="form-input" placeholder="UF_CRM_BONUS_USED"
                style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
                value={dealBonusUsedField} onChange={e => setDealBonusUsedField(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Поле сделки: начислено бонусов</label>
              <input className="form-input" placeholder="UF_CRM_BONUS_EARNED"
                style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
                value={dealBonusEarnedField} onChange={e => setDealBonusEarnedField(e.target.value)} />
            </div>
          </div>

          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, padding: 10, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)' }}>
            <strong>Как узнать код поля в Б24:</strong> Настройки → CRM → Настройки форм и отчётов → Пользовательские поля → Контакты/Сделки. Коды пользовательских полей начинаются с <code>UF_CRM_</code>
          </div>
        </div>
      )}


      {integration && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Webhook для входящих событий</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            Настройте в Б24 исходящий вебхук на этот адрес (события: ONCRMDEALUPDATE, ONCRMDEALADD)
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <code style={{
              flex: 1, padding: '10px 14px', background: 'var(--bg-secondary)',
              borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
              fontSize: 11, fontFamily: 'var(--font-mono)', overflowX: 'auto', whiteSpace: 'nowrap',
            }}>
              {window.location.origin}/api/v1/crm/webhook/{integration.id}
            </code>
            <button className="btn btn-ghost btn-sm" onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/api/v1/crm/webhook/${integration.id}`);
              toast.success('Скопировано');
            }}>
              <Copy size={14} />
            </button>
          </div>
        </div>
      )}


      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={16} /> {saving ? 'Сохранение...' : (integration ? 'Сохранить' : 'Подключить')}
        </button>
        {integration && (
          <>
            <button className="btn btn-ghost" onClick={() => handleImportClients(0)} disabled={importing}>
              <Users size={16} /> {importing ? 'Импорт...' : 'Импорт всех клиентов'}
            </button>
            <button className="btn btn-ghost" onClick={() => handleImportOrders({ only_new: true })} disabled={importing}>
              <ShoppingCart size={16} /> {importing ? 'Импорт...' : 'Импорт новых сделок'}
            </button>
            <button className="btn btn-ghost" onClick={() => handleImportOrders({})} disabled={importing}>
              <ShoppingCart size={16} /> Полный реимпорт
            </button>
            <button className="btn btn-danger" onClick={handleDelete}>
              <Trash2 size={14} /> Удалить
            </button>
          </>
        )}
      </div>


      {importResult && (
        <div className="card" style={{ padding: 14, fontSize: 13, lineHeight: 1.8 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {importResult.running && (
                <span className="loader" style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid var(--accent)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></span>
              )}
              {importResult.running ? 'Импорт выполняется...' : `Результат импорта ${importResult.type === 'clients' ? 'клиентов' : 'заказов'}`}
            </div>
            {importResult.running && currentTaskId && (
              <button className="btn btn-danger btn-sm" onClick={handleCancelImport}>
                Остановить
              </button>
            )}
          </div>
          <div>Обработано: <strong>{importResult.total || 0}</strong></div>
          <div style={{ color: 'var(--success)' }}>Создано: {importResult.created || 0}</div>
          <div style={{ color: 'var(--text-muted)' }}>Обновлено: {importResult.updated || 0}</div>
          {importResult.errors > 0 && (
            <div style={{ color: 'var(--warning)' }}>Ошибок: {importResult.errors}</div>
          )}
          {importResult.error_samples && importResult.error_samples.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', color: 'var(--warning)', fontSize: 11 }}>
                Показать примеры ошибок ({importResult.error_samples.length})
              </summary>
              <div style={{ fontSize: 11, marginTop: 6, maxHeight: 200, overflow: 'auto', fontFamily: 'var(--font-mono)', color: 'var(--danger)' }}>
                {importResult.error_samples.map((e, i) => <div key={i} style={{ padding: '2px 0' }}>{e}</div>)}
              </div>
            </details>
          )}
          {importResult.message && importResult.status === 'failed' && (
            <div style={{ color: 'var(--danger)', marginTop: 8 }}>{importResult.message}</div>
          )}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {integration?.last_sync_at && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12 }}>
          Последняя синхронизация: {new Date(integration.last_sync_at + 'Z').toLocaleString('ru')}
        </div>
      )}
    </div>
  );
}